import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageScrollListItem = {
  image: string;
  title: LocalizedString;
  linkUrl?: string;
};

export type PageScrollListSectionConfig = {
  enabled?: boolean;
  heading?: LocalizedString;
  roundness?: number; // 0..50 (percent)
  itemSize?: number; // px
  gap?: number; // px
  items?: PageScrollListItem[];
};

type Props = {
  config?: PageScrollListSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const clamp = (n: any, min: number, max: number) => Math.max(min, Math.min(max, Number(n) || 0));

const PageScrollListSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageScrollListSectionConfig = (config && typeof config === 'object') ? config : {};

  const { language } = useTranslation();

  const heading = getLocalizedString(cfg.heading, language, 'en', 'Neues Jahr, neue Lieblinge');
  const roundness = clamp(cfg.roundness, 0, 50);
  const itemSize = clamp(cfg.itemSize, 72, 180) || 112;
  const gap = clamp(cfg.gap, 6, 28) || 18;

  const resolve = React.useCallback((p?: string) => {
    if (!p) return '';
    return resolvePublicUrl ? resolvePublicUrl(p) : p;
  }, [resolvePublicUrl]);

  const items: PageScrollListItem[] = Array.isArray(cfg.items)
    ? cfg.items
      .filter((it: any) => it && typeof it === 'object' && typeof it.image === 'string' && it.title)
      .map((it: any) => ({
        image: String(it.image || ''),
        title: (typeof it.title === 'string' || (it.title && typeof it.title === 'object')) ? it.title : { de: '' },
        linkUrl: typeof it.linkUrl === 'string' ? it.linkUrl : '',
      }))
      .filter((it: any) => it.image && getLocalizedString(it.title, language, 'en', ''))
    : [];

  if (cfg.enabled === false) return null;

  const radiusCss = roundness >= 50 ? '50%' : `${roundness}%`;

  return (
    <Box sx={{ py: { xs: 5, md: 7 } }}>
      <Container maxWidth="lg">
        <Typography variant="h5" sx={{ fontWeight: 950, letterSpacing: -0.2, mb: 2.5 }}>
          {heading}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: `${gap}px`,
            overflowX: 'auto',
            py: 1,
            pr: 1,
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(2,6,23,0.14)', borderRadius: 999 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(2,6,23,0.06)', borderRadius: 999 },
          }}
        >
          {items.length === 0 ? (
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 3, p: 3, minWidth: 260, bgcolor: 'background.paper' }}>
              <Typography variant="body2" color="text.secondary">Noch keine Items.</Typography>
            </Box>
          ) : (
            items.map((it, idx) => {
              const title = getLocalizedString(it.title, language, 'en', '');
              const clickable = Boolean(it.linkUrl);
              return (
                <Box
                  key={`${it.image}-${idx}`}
                  component={clickable ? 'a' : 'div'}
                  href={clickable ? it.linkUrl : undefined}
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    scrollSnapAlign: 'start',
                    minWidth: itemSize + 20,
                    maxWidth: itemSize + 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1,
                    '&:hover .scrollItemImg': clickable ? { transform: 'translateY(-2px) scale(1.02)' } : undefined,
                  }}
                >
                  <Box
                    className="scrollItemImg"
                    sx={{
                      width: itemSize,
                      height: itemSize,
                      borderRadius: radiusCss,
                      overflow: 'hidden',
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 16px 40px rgba(2,6,23,0.10)',
                      transition: 'transform 200ms ease',
                    }}
                  >
                    <Box
                      component="img"
                      src={resolve(it.image)}
                      alt={title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 900,
                      textAlign: 'center',
                      lineHeight: 1.2,
                      maxWidth: itemSize + 30,
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PageScrollListSection;
